import React, { useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Badge, Button, ButtonBar, Card, Dialog } from '@ygtechnologies/rubus-ui';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';

import SelectedCorner from '@/buyer/components/SelectedCorner/SelectedCorner';
import SmallAttachmentList from '@/components/SmallAttachmentList';
import { ProductThumbnails } from '@/seller/components/products/ProductThumbnails';
import { ConfiguratorGroupDto, ConfiguratorOptionDto, ConfiguratorPriceDto } from '@/services/configurator';
import { previewSelection } from '@/services/configurator/configurator.api';
import { useConfiguratorMutations } from '@/components/OptionConfigurator/ConfiguratorMutationContext';
import { useOptionConfigurator } from '@/components/OptionConfigurator/OptionConfiguratorContext';
import { useImageUrls } from '@/components/OptionConfigurator/useImageUrls';
import { ProductType } from '@/types';
import { checkIfNCSCodeIsValid } from '@/utils/color';
import { classnames } from '@/utils/html';
import { useDebounce, useGetDefaultProjectUnitOfMeasurement, useShouldDisplayDefaultTextOnOptions } from '@/utils/hooks';
import { convertToUnit, unitAbbreviations } from '@/utils/unit-of-measurements';
import { TextView } from '@ygtechnologies/bygr-frontend-component-library';
import { BuyerOptionCardPrice } from '../../BuyerOptionCard/BuyerOptionCardPrice';
import { QuantityStepper } from '../../BuyerOptionCard/QuantityStepper';

import { findDependentGroups } from '../../dependencyHelpers';
import { getVisibleBuyerOptions } from '../../utils/visibleBuyerOptions';
import { useOptionsScope } from '../OptionsScope';
import { useOptionsData } from '../useOptionsData';
import { NcsCodeStep } from './NcsCodeStep';
import theme from './SelectionWizardDialog.module.scss';

// Manual quantity is whole-number; the group's `quantity` echoes the saved value (or the
// pricing-field/default of 1), so round it for the stepper's baseline.
const roundedGroupQuantity = (group: ConfiguratorGroupDto): number =>
    group.quantity != null ? Math.max(1, Math.round(group.quantity)) : 1;

interface Props {
    buyerOptionGroupId: number;
    /** v2 room id (0 = "general") needed to disambiguate per-room option groups. */
    roomId: number;
    isOpen: boolean;
    onClose: () => void;
    onSelectionChanged?: () => void;
}

export const SelectionWizardDialog: React.FC<Props> = ({
    buyerOptionGroupId,
    roomId,
    isOpen,
    onClose,
    onSelectionChanged,
}) => {
    const { readOnly } = useOptionsScope();
    const { findGroup, allGroups } = useOptionsData();
    const { selectOptions } = useConfiguratorMutations();
    const { isShowroom, residentialUnitId } = useOptionConfigurator();
    const { getProjectCatalogProductFileThumbnailUrl, getProjectCatalogProductFileUrl } = useImageUrls();

    const triggerGroup = findGroup(buyerOptionGroupId, roomId);

    // Trigger group first, then any dependent groups in the same room reachable
    // from its options. Same-room restriction mirrors v2's DependencyWizardModal.
    const steps = useMemo<ConfiguratorGroupDto[]>(() => {
        if (!triggerGroup) return [];
        const sameRoomGroups = allGroups.filter((g) => g.roomId === triggerGroup.roomId);
        return [triggerGroup, ...findDependentGroups(sameRoomGroups, triggerGroup)];
    }, [triggerGroup, allGroups]);

    const [stepIndex, setStepIndex] = useState(0);
    const [pendingByGroupId, setPendingByGroupId] = useState<Record<number, ConfiguratorOptionDto>>({});
    const [customFieldByGroupId, setCustomFieldByGroupId] = useState<Record<number, string>>({});
    const [quantityByGroupId, setQuantityByGroupId] = useState<Record<number, number>>({});
    const [touchedGroupIds, setTouchedGroupIds] = useState<Set<number>>(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const defaultUnit = useGetDefaultProjectUnitOfMeasurement();
    const showDefaultText = useShouldDisplayDefaultTextOnOptions();

    // Reset wizard state when the dialog opens. Keyed on the trigger group id so
    // background refetches don't clobber in-progress picks.
    useEffect(() => {
        if (!isOpen) return;
        setStepIndex(0);
        setSaveError(null);
        setTouchedGroupIds(new Set());
        const initialPending: Record<number, ConfiguratorOptionDto> = {};
        const initialCustom: Record<number, string> = {};
        const initialQuantity: Record<number, number> = {};
        for (const g of steps) {
            initialQuantity[g.id] = roundedGroupQuantity(g);
            if (g.selectedOption) {
                initialPending[g.id] = g.selectedOption;
                initialCustom[g.id] = g.selectedOption.customFieldData ?? '';
            }
        }
        setPendingByGroupId(initialPending);
        setCustomFieldByGroupId(initialCustom);
        setQuantityByGroupId(initialQuantity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, buyerOptionGroupId]);

    // Live (unsaved) price for the pending option as the buyer edits a manual quantity. The backend
    // recalculates with real buyer pricing without persisting; debounced so we don't call per keystroke.
    // Showroom has no authenticated preview endpoint, so it's skipped there.
    const [preview, setPreview] = useState<{ optionId: number; price: ConfiguratorPriceDto } | null>(null);
    const [previewInFlight, setPreviewInFlight] = useState(false);
    const previewGroup = steps[stepIndex];
    const previewOption = previewGroup ? pendingByGroupId[previewGroup.id] : undefined;
    const savedPreviewQuantity = previewGroup ? roundedGroupQuantity(previewGroup) : null;
    const previewQuantity = previewGroup?.allowManualQuantity ? quantityByGroupId[previewGroup.id] ?? null : null;
    const debouncedPreviewQuantity = useDebounce(previewQuantity, 300);

    // Only preview when the quantity actually differs from the saved value — otherwise opt.price already
    // reflects it (and we skip a redundant call + spinner flash on open).
    const previewActive =
        !isShowroom &&
        !!previewGroup?.allowManualQuantity &&
        !!previewOption &&
        previewQuantity != null &&
        previewQuantity !== savedPreviewQuantity;

    useEffect(() => {
        if (!previewActive || !previewGroup || !previewOption || debouncedPreviewQuantity == null) {
            setPreview(null);
            setPreviewInFlight(false);
            return;
        }
        let cancelled = false;
        setPreviewInFlight(true);
        previewSelection(residentialUnitId, previewGroup.deadlineId, {
            GroupId: previewGroup.id,
            SelectedOptionId: previewOption.id,
            RoomId: previewGroup.roomId === 0 ? null : previewGroup.roomId,
            CustomFieldData: customFieldByGroupId[previewGroup.id] || null,
            ManualQuantity: debouncedPreviewQuantity,
        })
            .then((data) => {
                if (cancelled) return;
                const group = data.groups.find((g) => g.id === previewGroup.id && g.roomId === previewGroup.roomId);
                const option = group?.options.find((o) => o.id === previewOption.id);
                if (option?.price) {
                    setPreview({ optionId: previewOption.id, price: option.price });
                }
            })
            .catch(() => {
                if (!cancelled) setPreview(null);
            })
            .finally(() => {
                if (!cancelled) setPreviewInFlight(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        previewActive,
        residentialUnitId,
        previewGroup?.id,
        previewGroup?.roomId,
        previewGroup?.deadlineId,
        previewOption?.id,
        debouncedPreviewQuantity,
    ]);

    // True from the moment the buyer changes the quantity until the matching recalculated price arrives.
    const previewLoading = previewActive && (previewQuantity !== debouncedPreviewQuantity || previewInFlight);

    if (!isOpen) return null;

    if (!triggerGroup) {
        return (
            <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <Dialog.Content contentWidth="md">
                    <Dialog.Header>
                        <Trans>Loading…</Trans>
                    </Dialog.Header>
                    <LoadingIndicator />
                </Dialog.Content>
            </Dialog>
        );
    }

    const currentGroup = steps[stepIndex];
    const isLastStep = stepIndex === steps.length - 1;
    const isFirstStep = stepIndex === 0;

    // Constraints from earlier steps only — we deliberately ignore selections from
    // later steps so a pre-populated downstream pick doesn't constrain the current one.
    const knownSelections: Record<number, ConfiguratorOptionDto> = {};
    for (let i = 0; i < stepIndex; i++) {
        const g = steps[i];
        const sel = pendingByGroupId[g.id];
        if (sel) knownSelections[g.id] = sel;
    }

    const pendingOption = pendingByGroupId[currentGroup.id] ?? null;

    const isOptionIncompatible = (option: ConfiguratorOptionDto) =>
        option.dependencyConflicts.some((c) => knownSelections[c.groupId]?.id === c.optionId);

    const pendingIsIncompatible = pendingOption !== null && isOptionIncompatible(pendingOption);
    // In the showroom, deadlines/signing are irrelevant, so locked/expired groups stay editable.
    const canChange = !readOnly && currentGroup.applicable && (isShowroom || !currentGroup.isLocked);

    const pendingNcsValue = customFieldByGroupId[currentGroup.id] ?? '';
    const pendingIsCustomPaint = pendingOption?.product?.type === ProductType.CustomPaint;
    const pendingNcsInvalid = pendingIsCustomPaint && pendingNcsValue.length > 0 && !checkIfNCSCodeIsValid(pendingNcsValue);

    // Mirror the legacy wizard: show options that are still valid given earlier steps,
    // plus this group's saved selection. Anchoring on the *saved* pick (not the pending
    // one) means a selection that became incompatible stays visible (flagged) to be
    // changed and doesn't vanish the instant a valid option is picked — while unrelated
    // incompatible options just drop out.
    const visibleOptions = getVisibleBuyerOptions(currentGroup, isShowroom).filter(
        (opt) => isFirstStep || !isOptionIncompatible(opt) || currentGroup.selectedOption?.id === opt.id
    );

    const handleSelect = (option: ConfiguratorOptionDto) => {
        setPendingByGroupId((prev) => ({ ...prev, [currentGroup.id]: option }));
        setTouchedGroupIds((prev) => {
            if (prev.has(currentGroup.id)) return prev;
            const next = new Set(prev);
            next.add(currentGroup.id);
            return next;
        });
    };

    const handleCustomField = (value: string) => {
        setCustomFieldByGroupId((prev) => ({ ...prev, [currentGroup.id]: value }));
        setTouchedGroupIds((prev) => {
            if (prev.has(currentGroup.id)) return prev;
            const next = new Set(prev);
            next.add(currentGroup.id);
            return next;
        });
    };

    const handleQuantity = (value: number) => {
        setQuantityByGroupId((prev) => ({ ...prev, [currentGroup.id]: value }));
        setTouchedGroupIds((prev) => {
            if (prev.has(currentGroup.id)) return prev;
            const next = new Set(prev);
            next.add(currentGroup.id);
            return next;
        });
    };

    const handleNext = () => setStepIndex((i) => i + 1);
    const handleBack = () => setStepIndex((i) => i - 1);

    const handleSave = async () => {
        setIsSubmitting(true);
        setSaveError(null);
        try {
            // Collect every touched, actually-changed group into one batch. Each entry carries its
            // group's own deadline since the legacy page spans all deadlines, and the shared
            // mutation provider (API or Showroom) applies them and updates the cache.
            const batch = steps
                .filter((g) => touchedGroupIds.has(g.id))
                .flatMap((g) => {
                    const option = pendingByGroupId[g.id];
                    if (!option) return [];
                    const savedQuantity = roundedGroupQuantity(g);
                    const pendingQuantity = g.allowManualQuantity ? (quantityByGroupId[g.id] ?? savedQuantity) : savedQuantity;
                    if (
                        g.selectedOption?.id === option.id &&
                        (g.selectedOption?.customFieldData ?? '') === (customFieldByGroupId[g.id] ?? '') &&
                        pendingQuantity === savedQuantity
                    ) {
                        return [];
                    }
                    return [
                        {
                            option,
                            customFieldData: customFieldByGroupId[g.id] || null,
                            roomId: g.roomId === 0 ? null : g.roomId,
                            deadlineId: g.deadlineId,
                            quantity: g.allowManualQuantity ? pendingQuantity : null,
                        },
                    ];
                });
            if (batch.length > 0) {
                await selectOptions(batch);
            }
            onSelectionChanged?.();
            onClose();
        } catch (err: unknown) {
            let message = 'Failed to save selection.';
            if (err instanceof Response) {
                try {
                    const body = await err.clone().text();
                    message = body || `${err.status} ${err.statusText}`;
                } catch {
                    message = `${err.status} ${err.statusText}`;
                }
            } else if (err instanceof Error) {
                message = err.message;
            }
            console.error('Option save failed', err);
            setSaveError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const header = steps.length > 1 ? `${currentGroup.name} (${stepIndex + 1} / ${steps.length})` : currentGroup.name;

    const unitAbbreviation = defaultUnit ? unitAbbreviations[defaultUnit] : null;
    const dimensionFields = [
        { key: 'widthCm', label: <Trans>Width</Trans> },
        { key: 'heightCm', label: <Trans>Height</Trans> },
        { key: 'depthCm', label: <Trans>Depth</Trans> },
        { key: 'lengthCm', label: <Trans>Length</Trans> },
    ] as const;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>{header}</Dialog.Header>
                {saveError && <AlertBanner type="danger">{saveError}</AlertBanner>}
                {!isFirstStep && (
                    <AlertBanner type={pendingIsIncompatible ? 'danger' : 'warning'}>
                        <Trans>
                            Your selection in <strong>{steps[0].name}</strong> affects this option group. Please review your
                            selection below.
                        </Trans>
                    </AlertBanner>
                )}

                {currentGroup.providedByProfile && (
                    <div className={theme.providerName}>{currentGroup.providedByProfile.name}</div>
                )}

                {currentGroup.description && <p className={theme.description}>{currentGroup.description}</p>}

                <div className={theme.optionList}>
                    {visibleOptions.map((opt) => {
                        // Incompatibility is pending-aware (computed against earlier steps), never
                        // the server's disabledByDependencies/selectable — those bake in the *saved*
                        // selection of the linked group, which the wizard reconciles step by step.
                        // Incompatible options are dropped by `visibleOptions`; the only one that can
                        // still be incompatible here is the saved pick, flagged below so the user
                        // changes it (pendingIsIncompatible blocks Next/Save until they do). The first
                        // step has no earlier picks, so nothing is constrained there. Hidden/archived
                        // options are already filtered out server-side (ConfiguratorService).
                        const incompatible = !isFirstStep && isOptionIncompatible(opt);
                        const disabled =
                            !canChange ||
                            opt.optionStatus?.disabledByPresets ||
                            opt.optionStatus?.disabledByManualSelection ||
                            incompatible;
                        const checked = pendingOption?.id === opt.id;

                        let actionButton: React.ReactNode;
                        if (incompatible) {
                            actionButton = (
                                <Badge variant="danger" appearance="subtle" iconLeft={faTriangleExclamation}>
                                    <Trans>Not compatible</Trans>
                                </Badge>
                            );
                        } else if (checked) {
                            actionButton = (
                                <Button variant="primary" size="sm" disabled>
                                    <Trans>Option selected</Trans>
                                </Button>
                            );
                        } else {
                            actionButton = (
                                <Button
                                    variant="tertiary"
                                    size="sm"
                                    disabled={disabled}
                                    onClick={() => handleSelect(opt)}>
                                    <Trans>Select option</Trans>
                                </Button>
                            );
                        }

                        return (
                            <Card
                                key={opt.id}
                                variant="flat"
                                className={classnames(theme.optionCard, {
                                    [theme.selected]: checked,
                                })}>
                                <SelectedCorner visible={checked} />
                                <ProductThumbnails
                                    images={opt.product?.images ?? []}
                                    thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                                    imageUrlGenerator={getProjectCatalogProductFileUrl}
                                    className={theme.optionThumb}
                                    size={120}
                                />
                                <div className={theme.optionMain}>
                                    <strong className={theme.optionName}>
                                        {opt.productName}
                                        {showDefaultText && opt.isDefault && (
                                            <>
                                                {' '}
                                                <Trans>(Default)</Trans>
                                            </>
                                        )}
                                    </strong>
                                    {opt.product?.description && (
                                        <div className={theme.optionDescription}>
                                            <TextView>{opt.product.description}</TextView>
                                        </div>
                                    )}
                                    {opt.product?.productLink && (
                                        <a
                                            href={opt.product.productLink}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className={theme.productLink}>
                                            <Trans>Product link</Trans>
                                        </a>
                                    )}
                                    {!!defaultUnit && (
                                        <div className={theme.dimensions}>
                                            {dimensionFields.map((dim) => {
                                                const cm = opt.product?.[dim.key];
                                                if (!cm) return null;
                                                return (
                                                    <div key={dim.key}>
                                                        <strong>{dim.label}:</strong> {convertToUnit(cm, defaultUnit)}
                                                        {unitAbbreviation}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {opt.product?.attachments && opt.product.attachments.length > 0 && (
                                        <SmallAttachmentList
                                            urlGenerator={(att) => getProjectCatalogProductFileUrl(att.id)}
                                            attachments={opt.product.attachments}
                                        />
                                    )}
                                    {checked && canChange && pendingIsCustomPaint && (
                                        <NcsCodeStep value={pendingNcsValue} onChange={handleCustomField} />
                                    )}
                                </div>
                                <div className={theme.optionRight}>
                                    {!currentGroup.hidePrice && opt.price && (
                                        <BuyerOptionCardPrice
                                            option={opt}
                                            priceOverride={preview?.optionId === opt.id ? preview.price : undefined}
                                            loading={previewLoading && checked}
                                        />
                                    )}
                                    {actionButton}
                                    {checked && canChange && currentGroup.allowManualQuantity && (
                                        <div className={theme.quantityField}>
                                            <span className={theme.quantityLabel}>
                                                <Trans>Quantity</Trans>
                                            </span>
                                            <QuantityStepper
                                                value={quantityByGroupId[currentGroup.id] ?? roundedGroupQuantity(currentGroup)}
                                                disabled={isSubmitting}
                                                onChange={handleQuantity}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>

                <ButtonBar>
                    {!isFirstStep && (
                        <Button variant="tertiary" onClick={handleBack} disabled={isSubmitting}>
                            <Trans>Back</Trans>
                        </Button>
                    )}
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={onClose} disabled={isSubmitting}>
                            <Trans>Cancel</Trans>
                        </Button>
                        {isLastStep ? (
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                loading={isSubmitting}
                                disabled={
                                    !canChange ||
                                    isSubmitting ||
                                    !pendingOption ||
                                    pendingIsIncompatible ||
                                    pendingNcsInvalid
                                }>
                                <Trans>Save</Trans>
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                onClick={handleNext}
                                disabled={!canChange || !pendingOption || pendingIsIncompatible || pendingNcsInvalid}>
                                <Trans>Next</Trans>
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
